/**
 * The `apply` of a FRESH waiting update, as observable state (S2-c).
 *
 * `register-sw`'s `onWaiting` hands React the function that applies the update (skip-waiting +
 * the one guarded reload). It fires OUTSIDE the React tree, so — like `pending.ts` — it publishes
 * to a tiny store rather than touching the DOM, and `<UpdatePrompt>` renders the redesign Confirm
 * from that state. This replaced a blocking `window.confirm` in `main.tsx`; the apply/reload
 * mechanism itself is unchanged.
 *
 * Distinct from `pending.ts`: THIS carries the callable apply for a reload that is still allowed;
 * `pending.ts` is the banner for when the session's one reload is already spent (no apply to offer).
 */

type Listener = (apply: (() => void) | null) => void;

let waiting: (() => void) | null = null;
const listeners = new Set<Listener>();

export function setWaitingApply(apply: (() => void) | null): void {
  waiting = apply;
  for (const listener of listeners) listener(apply);
}

export function subscribeWaitingApply(listener: Listener): () => void {
  listeners.add(listener);
  listener(waiting); // a late subscriber still gets an update that is already waiting
  return () => {
    listeners.delete(listener);
  };
}

/** Tests only: the store outlives a component, so it has to be resettable. */
export function resetWaitingApply(): void {
  waiting = null;
  listeners.clear();
}
